import React, { useState } from 'react';
import { hooks } from 'botframework-webchat-component';

const { useSendMessage } = hooks;

// Field labels for each card, keyed by language. The backend sends identical
// field keys for en/ar; only the labels + button titles differ.
const LABELS = {
  en: {
    premisetype: 'Premise Type',
    premisenumber: 'Premise Number',
    contractaccount: 'Contract Account Number',
    ejarinumber: 'Ejari Number',
    securitydepositamount: 'Security Deposit',
    reconnectionamount: 'Reconnection',
    innovationfee: 'Innovation Fee',
    knowledgefee: 'Knowledge Fee',
    addresschangefee: 'Address Change Fee',
    total_amount: 'Total',
    payAllTotal: 'Total Amount for All Contracts:',
    payAll: 'Pay All',
    pay: 'Pay',
    premiseFallback: 'Premise',
  },
  ar: {
    premisetype: 'نوع العقار',
    premisenumber: 'رقم العقار',
    contractaccount: 'رقم الحساب التعاقدي',
    ejarinumber: 'رقم إيجاري',
    securitydepositamount: 'مبلغ التأمين',
    reconnectionamount: 'مبلغ إعادة التوصيل',
    innovationfee: 'رسوم الابتكار',
    knowledgefee: 'رسوم المعرفة',
    addresschangefee: 'رسوم تغيير العنوان',
    total_amount: 'الإجمالي',
    payAllTotal: 'المبلغ الإجمالي لجميع العقود:',
    payAll: 'ادفع الكل',
    pay: 'ادفع',
    premiseFallback: 'عقار',
  },
};

// Detail rows inside the header box, in display order. Premise Type and Address
// are shown separately as the card title/subtitle.
const HEADER_DETAIL_FIELDS = ['premisenumber', 'contractaccount', 'ejarinumber'];

// Identifier fields: stay left-to-right even in an RTL layout and get a copy button.
const COPYABLE_FIELDS = new Set(['premisenumber', 'contractaccount', 'ejarinumber']);

// Fee lines shown only when > 0 (total_amount is always shown separately).
const FEE_FIELDS = ['securitydepositamount', 'reconnectionamount', 'innovationfee', 'knowledgefee', 'addresschangefee'];

const hasTextValue = (v) => v !== undefined && v !== null && String(v).trim() !== '';

const formatAmount = (amount) =>
  (Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Dirham glyph: the letter "D" rendered in the bundled AED-Regular font (loaded
// globally by MessageItem). Falls back to the currency code for non-AED amounts.
const Money = ({ amount, currency, weight = 600 }) => (
  <span dir="ltr" style={{ fontWeight: weight, whiteSpace: 'nowrap', color: '#111' }}>
    {!currency || currency === 'AED' ? (
      <span style={{ fontFamily: "'AED-Regular', 'Arial', sans-serif", marginInlineEnd: '4px' }}>D</span>
    ) : (
      <span style={{ marginInlineEnd: '4px' }}>{currency}</span>
    )}
    {formatAmount(amount)}
  </span>
);

const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#4b5563" aria-hidden="true">
    <path d="M12 3 2 11h3v9h5v-6h4v6h5v-9h3z" />
  </svg>
);

const CopyIcon = ({ done }) =>
  done ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007560" strokeWidth="2.5" aria-hidden="true">
      <path d="m5 12 5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007560" strokeWidth="2" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );

const PendingActionCards = ({ data, isArabic: isArabicProp, locked = false }) => {
  const sendMessage = useSendMessage();
  const [submitted, setSubmitted] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  if (!data || !Array.isArray(data.cards) || data.cards.length === 0) return null;

  const isArabic = (data.language || (isArabicProp ? 'ar' : 'en')) === 'ar';
  const labels = LABELS[isArabic ? 'ar' : 'en'];
  const dir = isArabic ? 'rtl' : 'ltr';
  const disabled = locked || submitted;

  const itemCards = data.cards.filter((c) => c.type === 'item');
  const payAllCard = data.cards.find((c) => c.type === 'payAll');

  // On click send payButton.value (NOT the localized title); the backend's
  // select_pending_accounts resolves "Pay Premise <n>" / "Pay All" to accounts.
  const handlePay = (card) => {
    const value = card?.payButton?.value;
    if (!value || disabled) return;
    setSubmitted(true);
    sendMessage(value);
  };

  const handleCopy = (key, value) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(String(value));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
  };

  const styles = {
    container: { direction: dir, width: '80%', marginRight: 'auto', marginTop: '8px' },
    card: { border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px', marginBottom: '12px', backgroundColor: '#fff' },
    headerBox: { backgroundColor: '#f4f6f6', borderRadius: '10px', padding: '14px', marginBottom: '4px' },
    headerTop: { display: 'flex', alignItems: 'center', gap: '12px' },
    avatar: { flexShrink: 0, width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontWeight: 700, fontSize: '16px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    subtitle: { fontSize: '13px', color: '#6b7280', marginTop: '2px' },
    headerDivider: { borderTop: '1px solid #e2e6e6', margin: '12px 0' },
    detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '5px 0', fontSize: '14px' },
    detailLabel: { color: '#4b5563' },
    detailValueWrap: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
    detailValue: { fontWeight: 600, color: '#111' },
    copyBtn: { display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 0 },
    feeRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '6px 2px', fontSize: '14px', color: '#4b5563' },
    totalRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', borderTop: '1px solid #e5e7eb', marginTop: '6px', padding: '12px 2px 0', fontSize: '15px' },
    totalLabel: { fontWeight: 700, color: '#111' },
    payBtn: { width: '100%', marginTop: '14px', padding: '11px', backgroundColor: disabled ? '#94c3ba' : '#007560', color: '#fff', border: 'none', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 600 },
    payAllCard: { border: '1px solid #007560', backgroundColor: '#f2f8f6' },
  };

  const renderDetailRow = (card, field, idx) => {
    if (!hasTextValue(card[field])) return null;
    const copyable = COPYABLE_FIELDS.has(field);
    const copyKey = `${idx}-${field}`;
    return (
      <div style={styles.detailRow} key={field}>
        <span style={styles.detailLabel}>{labels[field]}</span>
        <span style={styles.detailValueWrap}>
          <span style={styles.detailValue} dir={copyable ? 'ltr' : dir}>{card[field]}</span>
          {copyable && (
            <button type="button" style={styles.copyBtn} onClick={() => handleCopy(copyKey, card[field])} title="Copy" aria-label="Copy">
              <CopyIcon done={copiedKey === copyKey} />
            </button>
          )}
        </span>
      </div>
    );
  };

  const renderFees = (card) =>
    FEE_FIELDS.map((field) => {
      const amount = Number(card[field]) || 0;
      if (amount <= 0) return null;
      return (
        <div style={styles.feeRow} key={field}>
          <span>{labels[field]}</span>
          <Money amount={amount} currency={card.currency} />
        </div>
      );
    });

  const renderPayButton = (card, fallbackLabel) => (
    <button type="button" style={styles.payBtn} disabled={disabled || !card?.payButton?.value} onClick={() => handlePay(card)}>
      {card?.payButton?.title || fallbackLabel}
    </button>
  );

  const renderItemCard = (card, idx) => {
    // Title priority: premise type, then room number.
    const titleValue = hasTextValue(card.premisetype)
      ? card.premisetype
      : (hasTextValue(card.roomnumber) ? card.roomnumber : labels.premiseFallback);
    // Room numbers are identifiers → render LTR; only when shown as the title.
    const titleIsRoomNumber = !hasTextValue(card.premisetype) && hasTextValue(card.roomnumber);
    return (
      <div style={styles.card} key={card.premisenumber || card.contractaccount || idx}>
        <div style={styles.headerBox}>
          <div style={styles.headerTop}>
            <span style={styles.avatar}><HomeIcon /></span>
            <div style={{ minWidth: 0 }}>
              <div style={styles.title} dir={titleIsRoomNumber ? 'ltr' : dir}>{titleValue}</div>
              {hasTextValue(card.address) && <div style={styles.subtitle}>{card.address}</div>}
            </div>
          </div>
          <div style={styles.headerDivider} />
          {HEADER_DETAIL_FIELDS.map((f) => renderDetailRow(card, f, idx))}
        </div>
        {renderFees(card)}
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>{labels.total_amount}</span>
          <Money amount={card.total_amount} currency={card.currency} weight={700} />
        </div>
        {renderPayButton(card, labels.pay)}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {itemCards.map((card, idx) => renderItemCard(card, idx))}

      {payAllCard && data.payAllAvailable && (
        <div style={{ ...styles.card, ...styles.payAllCard }}>
          <div style={{ ...styles.totalRow, borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
            <span style={styles.totalLabel}>{labels.payAllTotal}</span>
            <Money amount={payAllCard.total_amount} currency={payAllCard.currency} weight={700} />
          </div>
          {renderPayButton(payAllCard, labels.payAll)}
        </div>
      )}
    </div>
  );
};

export default PendingActionCards;
